import { writable, derived, get } from 'svelte/store';
import { liveQuery } from 'dexie';
import { db, type Person, type Relationship, type Tree } from './db';
import { authState } from './auth';
import { logAudit } from './audit';
import { newId } from './ids';
import { isLiving } from './living';

// ---------- Route (minimal hash router) ----------

export type Route =
  | { name: 'home' }
  | { name: 'tree'; treeId: string; personId?: string }
  | { name: 'admin' }
  | { name: 'audit'; treeId: string };

function parseHash(): Route {
  const h = location.hash.replace(/^#\/?/, '');
  if (!h) return { name: 'home' };
  const [first, second, third] = h.split('/');
  if (first === 'admin') return { name: 'admin' };
  if (first === 'tree' && second) return { name: 'tree', treeId: second, personId: third };
  if (first === 'audit' && second) return { name: 'audit', treeId: second };
  return { name: 'home' };
}

export const route = writable<Route>(parseHash());
window.addEventListener('hashchange', () => route.set(parseHash()));

export function go(path: string) {
  location.hash = path.startsWith('#') ? path : `#${path}`;
}

// ---------- Live collections ----------

export const currentTreeId = writable<string | null>(null);

export const myTrees = writable<Tree[]>([]);
liveQuery(async () => {
  const email = get(authState).email;
  if (!email) return [] as Tree[];
  const all = await db.trees.toArray();
  return all
    .filter((t) => t.owner === email || t.realmId)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}).subscribe((v) => myTrees.set(v));

export const currentTree = derived([currentTreeId, myTrees], ([$id, $trees]) =>
  $id ? $trees.find((t) => t.id === $id) ?? null : null,
);

export const currentPeople = writable<Person[]>([]);
export const currentRels = writable<Relationship[]>([]);

let peopleSub: { unsubscribe: () => void } | null = null;
let relsSub: { unsubscribe: () => void } | null = null;

currentTreeId.subscribe((id) => {
  peopleSub?.unsubscribe();
  relsSub?.unsubscribe();
  if (!id) { currentPeople.set([]); currentRels.set([]); return; }
  peopleSub = liveQuery(() => db.people.where('treeId').equals(id).toArray())
    .subscribe((v) => currentPeople.set(v));
  relsSub = liveQuery(() => db.relationships.where('treeId').equals(id).toArray())
    .subscribe((v) => currentRels.set(v));
});

// ---------- Mutations ----------

export async function createTree(title: string, description?: string): Promise<Tree> {
  const email = get(authState).email;
  if (!email) throw new Error('Sign in first.');
  const tree: Tree = {
    id: newId(),
    owner: email,
    title: title.trim() || 'Untitled tree',
    description,
    visibility: 'private',
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  await db.trees.add(tree);
  await logAudit('tree.create', { treeId: tree.id, after: tree });
  return tree;
}

export async function updateTree(id: string, patch: Partial<Tree>): Promise<void> {
  const before = await db.trees.get(id);
  await db.trees.update(id, { ...patch, updatedAt: Date.now() });
  const after = await db.trees.get(id);
  await logAudit('tree.update', { treeId: id, before, after });
}

export async function setTreeVisibility(id: string, visibility: 'public' | 'private'): Promise<void> {
  const before = await db.trees.get(id);
  await db.trees.update(id, { visibility, updatedAt: Date.now() });
  await logAudit(visibility === 'public' ? 'tree.publish' : 'tree.unpublish', {
    treeId: id, before, after: await db.trees.get(id),
  });
}

export async function deleteTree(id: string): Promise<void> {
  await db.transaction('rw', db.trees, db.people, db.relationships, db.comments, async () => {
    await db.trees.delete(id);
    await db.people.where('treeId').equals(id).delete();
    await db.relationships.where('treeId').equals(id).delete();
    await db.comments.where('treeId').equals(id).delete();
  });
}

export async function createPerson(treeId: string, init: Partial<Person> = {}): Promise<Person> {
  const email = get(authState).email ?? undefined;
  const p: Person = {
    id: newId(),
    treeId,
    name: init.name ?? 'New person',
    gender: init.gender ?? 'unknown',
    owner: email,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    ...init,
  };
  await db.people.add(p);
  await logAudit('person.create', { treeId, targetId: p.id, after: p });
  return p;
}

export async function updatePerson(id: string, patch: Partial<Person>): Promise<void> {
  const before = await db.people.get(id);
  if (!before) return;
  const after: Person = { ...before, ...patch, updatedAt: Date.now() };
  // Re-derive living unless user has explicitly overridden.
  if (!after.livingOverride) after.living = isLiving(after);
  await db.people.put(after);
  await logAudit('person.update', { treeId: before.treeId, targetId: id, before, after });
}

export async function deletePerson(id: string): Promise<void> {
  const before = await db.people.get(id);
  if (!before) return;
  await db.transaction('rw', db.people, db.relationships, async () => {
    await db.people.delete(id);
    await db.relationships.where('a').equals(id).delete();
    await db.relationships.where('b').equals(id).delete();
  });
  await logAudit('person.delete', { treeId: before.treeId, targetId: id, before });
}

export async function linkParent(parentId: string, childId: string): Promise<void> {
  if (parentId === childId) return;
  const existing = await db.relationships
    .where('a').equals(parentId)
    .and((r) => r.kind === 'parent' && r.b === childId)
    .first();
  if (existing) return;
  const child = await db.people.get(childId);
  if (!child) return;
  const rel: Relationship = {
    id: newId(), treeId: child.treeId, kind: 'parent',
    a: parentId, b: childId,
    createdAt: Date.now(), updatedAt: Date.now(),
  };
  await db.relationships.add(rel);
  await logAudit('rel.create', { treeId: child.treeId, targetId: rel.id, after: rel });
}

export async function linkSpouse(a: string, b: string): Promise<void> {
  if (a === b) return;
  const [sa, sb] = [a, b].sort();
  const existing = await db.relationships
    .where('kind').equals('spouse')
    .and((r) => (r.a === sa && r.b === sb) || (r.a === sb && r.b === sa))
    .first();
  if (existing) return;
  const p = await db.people.get(sa);
  if (!p) return;
  const rel: Relationship = {
    id: newId(), treeId: p.treeId, kind: 'spouse', a: sa, b: sb,
    createdAt: Date.now(), updatedAt: Date.now(),
  };
  await db.relationships.add(rel);
  await logAudit('rel.create', { treeId: p.treeId, targetId: rel.id, after: rel });
}

export async function unlink(id: string): Promise<void> {
  const before = await db.relationships.get(id);
  if (!before) return;
  await db.relationships.delete(id);
  await logAudit('rel.delete', { treeId: before.treeId, targetId: id, before });
}

// ---------- Selected person ----------
export const selectedPersonId = writable<string | null>(null);
export const selectedPerson = derived([selectedPersonId, currentPeople], ([$id, $people]) =>
  $id ? $people.find((p) => p.id === $id) ?? null : null,
);
